import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, User, Shield, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SessionLogs = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load session logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-3xl font-bold text-green-800">
              <Clock className="inline mr-2" size={32} />
              Session Logs
            </h1>
          </div>
          <Button onClick={fetchSessions} variant="outline" disabled={loading}>
            <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border">
            <p className="text-gray-500 text-sm">Total Sessions</p>
            <p className="text-2xl font-bold text-green-700">{sessions.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <p className="text-gray-500 text-sm">Active Sessions</p>
            <p className="text-2xl font-bold text-blue-700">
              {sessions.filter(s => !isExpired(s.expires_at)).length}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border">
            <p className="text-gray-500 text-sm">Expired Sessions</p>
            <p className="text-2xl font-bold text-gray-500">
              {sessions.filter(s => isExpired(s.expires_at)).length}
            </p>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-green-100">
                <tr>
                  <th className="p-3 text-left text-sm font-semibold text-green-800">Username</th>
                  <th className="p-3 text-left text-sm font-semibold text-green-800">Role</th>
                  <th className="p-3 text-left text-sm font-semibold text-green-800">Login Time</th>
                  <th className="p-3 text-left text-sm font-semibold text-green-800">Expires At</th>
                  <th className="p-3 text-left text-sm font-semibold text-green-800">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-gray-500">
                      <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                      Loading sessions...
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-gray-500">
                      No session logs found
                    </td>
                  </tr>
                ) : (
                  sessions.map((session, index) => {
                    const expired = isExpired(session.expires_at);
                    return (
                      <tr key={session.id || index} className="border-b hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center">
                            <User className="text-gray-400 mr-2" size={18} />
                            <span className="font-medium">{session.username}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            session.role === 'admin' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {session.role === 'admin' && <Shield className="inline mr-1" size={12} />}
                            {session.role}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {formatDate(session.login_time)}
                        </td>
                        <td className="p-3 text-sm text-gray-600">
                          {formatDate(session.expires_at)}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            expired 
                              ? 'bg-gray-100 text-gray-600' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {expired ? 'Expired' : 'Active'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-4 text-center">
          Sessions automatically expire after 4 hours of inactivity.
        </p>
      </div>
    </div>
  );
};

export default SessionLogs;
